import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Home, 
  UserCheck, 
  Wallet, 
  Truck,
  BarChart3,
  Package
} from 'lucide-react';

const BottomNavigation = ({ 
  isOnline = true, 
  onAttendanceClick,
  onReportsClick,
  activeTab = 'dashboard',
  showReports = false,
  features = {} // Subscription-based features
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Feature check helper - returns true if feature is enabled or no features specified (legacy)
  const hasFeature = (featureName) => {
    if (Object.keys(features).length === 0) return true; // Legacy clients have all features
    
    // Map feature names (some variations exist in database)
    const featureMap = {
      'raw_materials': features.raw_materials === true,
      'reports': features.reports === true,
      'attendance': features.attendance === true || features.staff_management === true, // attendance is part of staff management
      'ledger_management': features.ledger_management === true || features.ledger === true // support both key names
    };
    
    return featureMap[featureName] ?? features[featureName] === true;
  };

  const handleReportsClick = () => {
    if (onReportsClick) {
      onReportsClick();
    } else {
      // Default behavior: navigate to dashboard with reports tab
      navigate('/?tab=reports');
    }
  };

  const handleHomeClick = () => {
    navigate('/?tab=dashboard');
  };

  // Define all nav items with their required features
  const allNavItems = [
    {
      id: 'dashboard',
      label: 'Home',
      icon: Home,
      onClick: handleHomeClick,
      isActive: location.pathname === '/' && activeTab === 'dashboard',
      feature: null // Always visible
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: Package,
      onClick: () => navigate('/franchise/inventory'),
      isActive: location.pathname === '/franchise/inventory',
      feature: null // Always visible
    },
    {
      id: 'order',
      label: 'Order',
      icon: Truck,
      onClick: () => navigate('/raw-material-order'),
      isActive: location.pathname === '/raw-material-order',
      feature: 'raw_materials'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      onClick: handleReportsClick,
      isActive: showReports || activeTab === 'reports',
      feature: 'reports'
    },
    {
      id: 'attendance',
      label: 'Attend',
      icon: UserCheck,
      onClick: onAttendanceClick,
      isActive: false,
      feature: 'attendance'
    },
    {
      id: 'ledger',
      label: 'Ledger',
      icon: Wallet,
      onClick: () => navigate('/ledger-management'),
      isActive: location.pathname === '/ledger-management',
      feature: 'ledger_management'
    }
  ];

  // Filter nav items based on enabled features
  const navItems = allNavItems.filter(item => 
    item.feature === null || hasFeature(item.feature)
  );

  // Calculate grid columns based on visible items
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6'
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border shadow-lg z-50 safe-area-bottom">
      <div className={`max-w-7xl mx-auto grid ${gridCols[navItems.length] || 'grid-cols-5'} gap-0.5 py-1 px-1`}>
        {navItems.map((item) => (
          <Button
            key={item.id}
            data-testid={`${item.id}-nav-btn`}
            variant={item.isActive ? 'default' : 'ghost'}
            className={`
              flex-col h-14 sm:h-12 px-1 sm:px-3 font-medium rounded-lg
              ${item.isActive ? 'bg-primary text-white hover:bg-primary/90' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}
            `}
            onClick={item.onClick}
          >
            <item.icon className="h-5 w-5 mb-0.5" />
            <span className="text-[10px] sm:text-xs uppercase tracking-wide leading-tight">
              {item.label}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default BottomNavigation;
